/* -----------------Globals--------------- */
import { useEffect } from "react";

import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
/* -----------------Helpers & Hooks--------------- */
import { EditorContent, useEditor } from "@tiptap/react";
import useEditorStore from "../../hooks/useEditorStore";

/* -----------------Child components--------------- */

import Highlight from "@tiptap/extension-highlight";
/* -----------------Types--------------- */
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import { CustomImageExtension } from "../tiptapextensions/ImageExtension";
import "./editor.css";
import { events } from "@constants/tracking";
import { TTSContainer } from "@memorang/features/src/TTS";
import { Box } from "@memorang/ui";
import { trackCustomEvent } from "analytics";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { HighlightExtension } from "./HighlightExtension";

type Props = {
	text: string;
	handleStoreHtml?: (html: string) => void;
	highlightedHtml?: string;
	isSummativeTest?: boolean;
	fontSize?: number;
	handleClickInsertableText?: (choiceId: string) => void;
	showTTS?: boolean;
	disableInsertableText?: boolean;
	itemId?: string;
};

const htmlEntityReplacements: { searchTerm: string; newValue: string }[] = [
	{ searchTerm: "&gte;", newValue: "&#8805;" },
	{ searchTerm: "&lte;", newValue: "&#8804;" },
	{ searchTerm: "&gt;", newValue: "&#62;" },
	{ searchTerm: "&lt;", newValue: "&#60;" },
	{ searchTerm: "&amp;", newValue: "&" },
];

const ItemStemMarkdownRenderer = ({
	text,
	fontSize = 16,
	handleClickInsertableText,
	showTTS,
	itemId,
	disableInsertableText,
}: {
	text: string;
	fontSize?: number;
	handleClickInsertableText?: (choiceId: string) => void;
	showTTS?: boolean;
	itemId?: string;
	disableInsertableText?: boolean;
}) => {
	const stemFontSize = useAppStore((store) => store.app.fontSizes?.stem);

	const finalStemFontSize = stemFontSize || 16;

	return (
		<Box flexDirection="row" gap={8} width="100%" alignItems="center">
			{!!itemId && showTTS && (
				<TTSContainer
					itemId={itemId}
					eventName={events.ttsClicked}
					trackCustomEvent={trackCustomEvent}
					explanation={text}
					mode={undefined}
					allowPrefetch={true}
				/>
			)}
			<Box flex={1}>
				<CustomMarkdownRenderer
					text={text}
					fontSize={finalStemFontSize || fontSize}
					variant="stem"
					handleClickInsertableText={handleClickInsertableText}
					disableInsertableText={disableInsertableText}
					paragraphMarginVertical={6}
				/>
			</Box>
		</Box>
	);
};

const ItemStemTipTap = ({ text, handleStoreHtml, highlightedHtml }: Props) => {
	const { setEditorRef } = useEditorStore();
	const theme = useAppTheme();
	const stemFontSize = useAppStore((store) => store.app.fontSizes?.stem);
	const appFont = useAppStore((store) => store.app.font) || "opensans";

	const finalStemFontSize = stemFontSize || 16;

	const editor = useEditor({
		extensions: [
			StarterKit,
			Markdown,
			CustomImageExtension,
			Highlight.configure({
				HTMLAttributes: {
					class: "highlight",
				},
			}),
			HighlightExtension,
		],
		content: "",
		editable: false,
		onUpdate: ({ editor: updateEditor }) => {
			if (handleStoreHtml) {
				handleStoreHtml(updateEditor.getHTML());
			}
		},
	});

	useEffect(() => {
		if (editor) {
			setEditorRef(editor);
		}
	}, [editor, setEditorRef]);

	useEffect(() => {
		if (editor) {
			let editorText = highlightedHtml || text;
			for (const { searchTerm, newValue } of htmlEntityReplacements) {
				editorText = editorText.replaceAll(searchTerm, newValue);
			}
			editor.commands.setContent(editorText);
		}
	}, [editor, highlightedHtml, text]);

	return (
		<>
			<style>
				{`
					.ProseMirror strong {
						font-family: "${appFont}-bold" !important;
						font-weight: normal;
					}
				`}
			</style>
			<EditorContent
				editor={editor}
				style={{
					color: theme.colors.text,
					fontFamily: `${appFont}-regular`,
					fontSize: finalStemFontSize,
				}}
			/>
		</>
	);
};
const ItemStem = ({
	text,
	handleStoreHtml,
	highlightedHtml,
	fontSize,
	isSummativeTest,
	handleClickInsertableText,
	disableInsertableText,
	itemId,
	showTTS,
}: Props) => {
	//TODO: Need to add highlighting support for markdown renderer, then we can remove tiptap
	if (!isSummativeTest) {
		return (
			<ItemStemMarkdownRenderer
				text={text}
				fontSize={fontSize}
				handleClickInsertableText={handleClickInsertableText}
				disableInsertableText={disableInsertableText}
				itemId={itemId}
				showTTS={showTTS}
			/>
		);
	}
	return (
		<ItemStemTipTap
			text={text}
			handleStoreHtml={handleStoreHtml}
			highlightedHtml={highlightedHtml}
		/>
	);
};

export default ItemStem;
